import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Typography, styled } from '@mui/material';

const Section = styled(Box)(({ theme }) => ({
    position: 'relative',
    paddingTop: theme.spacing(6.5),
    paddingBottom: theme.spacing(6.5),
    background: 'linear-gradient(135deg, #0066FF 0%, #6366F1 52%, #4F46E5 100%)',
    overflow: 'hidden',
    color: '#FFFFFF',
    '--ink': '#FFFFFF',
    '--muted': 'rgba(255, 255, 255, 0.92)',
    '--muted2': 'rgba(255, 255, 255, 0.86)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
            'radial-gradient(circle at 85% 18%, rgba(255, 255, 255, 0.16) 0%, transparent 55%), radial-gradient(circle at 12% 78%, rgba(255, 255, 255, 0.12) 0%, transparent 60%)',
        pointerEvents: 'none',
    },
    [theme.breakpoints.down('md')]: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
    },
    [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
    },
}));

const Wrap = styled(Box)({
    position: 'relative',
    zIndex: 1,
    maxWidth: '1040px',
    margin: '0 auto',
});

const Header = styled(Box)({
    textAlign: 'center',
    maxWidth: '900px',
    margin: '0 auto 18px',
});

const Kicker = styled(Typography)(({ theme }) => ({
    display: 'inline-block',
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    padding: theme.spacing(0.75, 1.75),
    borderRadius: 999,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        fontSize: '0.75rem',
        padding: theme.spacing(0.75, 2.25),
    },
}));

const Title = styled(Typography)(({ theme }) => ({
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.22,
    letterSpacing: '-0.03em',
    color: '#FFFFFF',
    margin: 0,
    [theme.breakpoints.up('sm')]: {
        fontSize: '2.125rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '2.5rem',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '2.75rem',
    },
}));

const Inner = styled(Box)({
    position: 'relative',
    zIndex: 1,
    maxWidth: '980px',
    margin: '0 auto',
    textAlign: 'center',
});

const StatsGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: '12px',
    marginBottom: '18px',
    '@media (max-width: 968px)': {
        gap: '10px',
    },
});

const Stat = styled(Box)({
    borderRadius: '16px',
    padding: '14px 14px',
    background: 'rgba(255, 255, 255, 0.10)',
    border: '1px solid rgba(255, 255, 255, 0.22)',
    boxShadow: '0 14px 34px rgba(15, 23, 42, 0.14)',
    backdropFilter: 'blur(10px)',
});

const StatValue = styled(Typography)({
    fontWeight: 700,
    fontSize: '20px',
    letterSpacing: '-0.02em',
    color: '#FFFFFF',
    lineHeight: 1.1,
});

const StatLabel = styled(Typography)({
    marginTop: '6px',
    fontWeight: 600,
    fontSize: '12px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.86)',
});

const Body = styled(Typography)(({ theme }) => ({
    color: 'rgba(255, 255, 255, 0.92)',
    fontSize: '1rem',
    lineHeight: 1.75,
    fontWeight: 400,
    margin: 0,
    textAlign: 'center',
    maxWidth: '920px',
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.0625rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '1.125rem',
    },
}));

// const Highlight = styled(Box)({
//     marginTop: '18px',
// });

const HighlightText = styled(Typography)({
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.75,
    letterSpacing: '-0.01em',
    color: '#FFFFFF',
    margin: 0,
    textAlign: 'center',
    '@media (max-width: 600px)': {
        fontSize: '0.9375rem',
    },
});

const CTA = styled(Box)({
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '22px',
});

const PrimaryButton = styled(Button)({
    border: '2px solid rgba(255, 255, 255, 0.4)',
    color: '#FFFFFF',
    background: 'transparent',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    padding: '13px 30px',
    borderRadius: '12px',
    minWidth: '200px',
    '&:hover': {
        borderColor: '#FFFFFF',
        background: 'rgba(55, 38, 154, 0.27)',
        color: '#FFFFFF',
        transform: 'translateY(-2px)',
    },
});

const SecondaryButton = styled(Button)({
    border: '2px solid rgba(255, 255, 255, 0.4)',
    color: '#FFFFFF',
    background: 'transparent',
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '16px',
    padding: '13px 30px',
    minWidth: '220px',
    '&:hover': {
        borderColor: '#FFFFFF',
        background: 'rgba(55, 38, 154, 0.27)',
        color: '#FFFFFF',
        transform: 'translateY(-2px)',
    },
});

const RealOperationsSection = () => {
    return (
        <Section component="section" aria-labelledby="company-real-ops-title">
            <Container maxWidth="lg">
                <Wrap>
                    <Header>
                        <Kicker component="p">Traction</Kicker>
                        <Title variant="h2" id="company-real-ops-title">
                            Real centres. Real operations
                        </Title>
                    </Header>

                    <Inner>
                        <StatsGrid aria-label="Operational metrics">
                            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / span 6', md: '1 / span 3' } }}>
                                <Stat>
                                    <StatValue>5</StatValue>
                                    <StatLabel>rehabilitation centres</StatLabel>
                                </Stat>
                            </Box>
                            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '7 / -1', md: '4 / span 3' } }}>
                                <Stat>
                                    <StatValue>1000+</StatValue>
                                    <StatLabel>sessions tracked/month</StatLabel>
                                </Stat>
                            </Box>
                            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / span 6', md: '7 / span 3' } }}>
                                <Stat>
                                    <StatValue>35</StatValue>
                                    <StatLabel>active therapists</StatLabel>
                                </Stat>
                            </Box>
                            <Box sx={{ gridColumn: { xs: '1 / -1', sm: '7 / -1', md: '10 / span 3' } }}>
                                <Stat>
                                    <StatValue>40%</StatValue>
                                    <StatLabel>MONTH ON MONTH GROWTH</StatLabel>
                                </Stat>
                            </Box>
                        </StatsGrid>

                        <Body component="p">
                            Every centre came in with the same situation: unverified sessions, disputed payouts,
                            documentation they couldn't stand behind. That's still the only problem we're focused
                            on.
                        </Body>

                        {/* <Highlight aria-label="Walkthrough highlight"> */}
                            <HighlightText component="p">
                                <br />
                                If you're done absorbing the cost of a system that was never built for this, talk to
                                us.
                                <br />
                                One walkthrough, 20 minutes.
                            </HighlightText>
                        {/* </Highlight> */}

                        <CTA>
                            <PrimaryButton 
                                component={RouterLink} to="/demo"  
                                id="Book-demo-button"
                                data-gtm="Book-demo-button" 
                                variant="outlined"
                            >
                                Book a walkthrough
                            </PrimaryButton>
                            <SecondaryButton
                                component="a"
                                href="https://wa.me/919187644794"
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="outlined"
                            >
                                Talk to us on WhatsApp
                            </SecondaryButton>
                        </CTA>
                    </Inner>
                </Wrap>
            </Container>
        </Section>
    );
};

export default RealOperationsSection;

